"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { connectWithThreads } from "@/app/api/social-connect";

export interface ThreadsAccount {
  id: string;
  username?: string;
  name?: string;
  profile_picture_url?: string;
  connectionId: string;
  connectionType: "threads";
}

export interface UseThreadsAccountsReturn {
  isLoading: boolean;
  isConnecting: boolean;
  accounts: ThreadsAccount[];
  error: string | null;
  loadAccounts: () => Promise<void>;
  connectThreads: (redirectTo?: string) => Promise<void>;
  disconnectThreads: (connectionId: string) => Promise<void>;
}

const THREADS_ACCOUNTS_ENDPOINT = "/api/social/accounts?types=threads";
const THREADS_DISCONNECT_ENDPOINT = "/api/threads/disconnect";

export function useThreadsAccounts(): UseThreadsAccountsReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [accounts, setAccounts] = useState<ThreadsAccount[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(THREADS_ACCOUNTS_ENDPOINT, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || "Failed to load Threads accounts");
      }
      const rawAccounts = Array.isArray((data as any)?.accounts) ? (data as any).accounts : [];
      const threads = rawAccounts.filter(
        (a: any): a is ThreadsAccount => a && a.connectionType === "threads"
      );
      setAccounts(threads);
    } catch (e: any) {
      setError(e?.message || "Failed to load Threads accounts");
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const connectThreads = useCallback(async (redirectTo?: string) => {
    setIsConnecting(true);
    try {
      await connectWithThreads(redirectTo ?? "/account");
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnectThreads = useCallback(
    async (connectionId: string) => {
      try {
        const res = await fetch(THREADS_DISCONNECT_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ connectionId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !(data as any)?.success) {
          throw new Error((data as any)?.error || "Failed to disconnect Threads account");
        }
        await loadAccounts();
      } catch (e: any) {
        setError(e?.message || "Failed to disconnect Threads account");
      }
    },
    [loadAccounts]
  );

  return useMemo(
    () => ({
      isLoading,
      isConnecting,
      accounts,
      error,
      loadAccounts,
      connectThreads,
      disconnectThreads,
    }),
    [isLoading, isConnecting, accounts, error, loadAccounts, connectThreads, disconnectThreads]
  );
}


